import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../../../shared/components/PageHeader';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ErrorAlert } from '../../../shared/components/ErrorAlert';
import { Spinner } from '../../../shared/components/Spinner';
import { useDocumentTitle } from '../../../shared/hooks/useDocumentTitle';
import { useAuth } from '../../../contexts/AuthContext';
import type { DownloadAlertRow, DownloadAlertFilters as DownloadAlertFilterValues } from '../../../models/downloadAlert';
import { fetchDownloadAlerts } from '../../../services/admin/downloadAlertsService';
import { fetchAdminDashboard } from '../../../services/admin/dashboardService';
import { DownloadAlertsFilters } from '../components/DownloadAlertsFilters';
import { DownloadAlertsGrid, type DownloadAlertGridRow } from '../components/DownloadAlertsGrid';
import type { AuditCompanyOption } from '../components/AuditFiltersPanel';

/**
 * Rows requested per load. The Syncfusion grid pages/sorts/filters this set client-side; server-side
 * filters (user, company, severity, alert type, date range) narrow it first. Matches the backend page cap.
 */
const MAX_ROWS = 200;

export function DownloadAlertsPage(): React.ReactElement {
  useDocumentTitle('Download alerts · Triplicity Backoffice');
  const { getAccessToken, acquireAccessToken } = useAuth();

  const [rows, setRows] = useState<DownloadAlertRow[]>([]);
  const [companies, setCompanies] = useState<AuditCompanyOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DownloadAlertFilterValues>({});

  const reload = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchDownloadAlerts(getAccessToken, filters, 1, MAX_ROWS);
      setRows(result.items);
    } catch (err) {
      setRows([]);
      setError(err instanceof Error ? err.message : 'Failed to load download alerts.');
    } finally {
      setLoading(false);
    }
  }, [getAccessToken, filters]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Company options for the filter picker (reuses the dashboard company list, same as API activity).
  useEffect(() => {
    let isMounted = true;
    void (async () => {
      try {
        const companyRows = await fetchAdminDashboard(acquireAccessToken);
        if (isMounted) {
          setCompanies(companyRows.map((c) => ({ companyId: c.companyId, companyName: c.companyName })));
        }
      } catch {
        if (isMounted) {
          setCompanies([]);
        }
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [acquireAccessToken]);

  const handleFilterChange = (next: DownloadAlertFilterValues): void => {
    setFilters(next);
  };

  const companyNameById = useMemo(() => {
    const map = new Map<string, string>();
    companies.forEach((c) => map.set(c.companyId, c.companyName));
    return map;
  }, [companies]);

  const gridRows = useMemo<DownloadAlertGridRow[]>(
    () =>
      rows.map((row) => ({
        ...row,
        companyName: (row.companyId && companyNameById.get(row.companyId)) || row.companyId || '',
      })),
    [rows, companyNameById]
  );

  const hasRows = rows.length > 0;

  return (
    <div>
      <PageHeader
        title="Download alerts"
        subtitle="Review mass-download detections raised by the API: bulk exports, repeated downloads, and other suspicious data-access patterns."
      />

      <DownloadAlertsFilters filters={filters} onChange={handleFilterChange} />

      {loading ? (
        <div className="bo-card bo-detail-loading">
          <Spinner label="Loading download alerts…" />
        </div>
      ) : null}

      {!loading && error ? <ErrorAlert message={error} /> : null}

      {!loading && !error && !hasRows ? (
        <EmptyState
          title="No download alerts"
          description="No download alerts match the current filters."
        />
      ) : null}

      {!loading && !error && hasRows ? <DownloadAlertsGrid rows={gridRows} /> : null}
    </div>
  );
}
